import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { SimpleInput } from 'app/shared/input';
import { MultiSelect } from 'app/shared/select';
import { Button } from 'app/shared/button';
import { DetailItem } from 'app/shared/info-section/item';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { Spacer } from 'app/layouts/generic';
import useValidator from 'hooks/validator';
import { useOrganizationService } from 'hooks/organization';
import { organizationValidationConfig } from 'app/shared/utils/form/validation/organization';
import { industries } from 'app/data/industries';
import { countries } from 'app/data/countries';

const COUNTRY_OPTIONS = countries.map((c) => ({ label: c.name, value: c.code }));

const CONTACT_CLASSES = [
  { label: '0 - 5,000', value: '0:5000' },
  { label: '5,001 - 20,000', value: '5001:20000' },
  { label: '20,001 - 50,000', value: '20001:50000' },
  { label: '50,001 - 100,000', value: '50001:100000' },
  { label: '100,001 - 250,000', value: '100001:250000' },
  { label: '250,001 - 300,000', value: '250001:300000' },
  { label: '300,001 - 400,000', value: '300001:400000' },
  { label: '400,001 - 500,000', value: '400001:500000' },
  { label: '500,001 - 1,000,000', value: '500001:1000000' },
  { label: '1,000,000+', value: '1000000' }
];

/**
 * Compact modal form for an agent to spin up a client organisation (tenant).
 * Mirrors the onboarding org form, plus an `agent_id` so the backend can tell
 * agent-owned tenants apart from regular ones. It does NOT touch the agent's
 * session/token — it only creates the tenant and reports back via `onCreated`.
 */
export const CreateOrganizationModal = ({ onCreated = () => {}, closeModal = () => {} }) => {
  const { email: user_email } = useSelector((state) => state.user_data);
  const agency = useSelector((state) => state.metadata.agency);
  const { createOrganization } = useOrganizationService();
  const { errors, validateField } = useValidator(organizationValidationConfig);

  const [saving, setSaving] = useState(false);
  const [selected_subscriber_count, setSelectedSubscriberCount] = useState(CONTACT_CLASSES[0]);
  const [selected_industry, setSelectedIndustry] = useState(industries[0]);
  const [selected_country, setSelectedCountry] = useState(null);
  const [form_data, setFormData] = useState({
    name: '',
    website: '',
    email: '',
    phone_number: '',
    street: '',
    state: '',
    zip: ''
  });

  const handleChange = (field, value) => {
    validateField(field, value);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const is_valid =
    !!form_data.name &&
    !errors.name &&
    !!form_data.website &&
    !errors.website &&
    !!form_data.email &&
    !errors.email &&
    !!form_data.phone_number &&
    !errors.phone_number &&
    !!selected_country &&
    !!form_data.street &&
    !!form_data.state &&
    !!form_data.zip;

  const submit = async () => {
    const data = {
      agent_id: agency?.id,
      emails: [form_data.email],
      industry: selected_industry.value,
      name: form_data.name,
      phone_number: form_data.phone_number,
      subscriber_count: selected_subscriber_count.value,
      user_email,
      website: form_data.website,
      address: {
        country: selected_country.value,
        street: form_data.street,
        state: form_data.state,
        zip: form_data.zip
      }
    };

    setSaving(true);
    const org = await createOrganization({ data });
    setSaving(false);

    if (!org) return;
    toast.success('Organisation created successfully.');
    setFormData({
      name: '',
      website: '',
      email: '',
      phone_number: '',
      street: '',
      state: '',
      zip: ''
    });
    setSelectedSubscriberCount(CONTACT_CLASSES[0]);
    setSelectedIndustry(industries[0]);
    setSelectedCountry(null);
    onCreated(org);
    closeModal();
  };

  return (
    <form>
      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="Organisation Name" required>
            <SimpleInput
              className="gm-input"
              value={form_data.name}
              onInput={(value) => handleChange('name', value)}
              error={errors.name}
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={3}>
        <GridColumn>
          <DetailItem title="Website" required>
            <SimpleInput
              className="gm-input"
              value={form_data.website}
              onInput={(value) => handleChange('website', value)}
              error={errors.website}
            />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Estimated List Size" required>
            <MultiSelect
              options={CONTACT_CLASSES}
              value={selected_subscriber_count}
              onChange={setSelectedSubscriberCount}
              isSorted={false}
            />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Industry" required>
            <MultiSelect
              options={industries}
              value={selected_industry}
              onChange={setSelectedIndustry}
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={2}>
        <GridColumn>
          <DetailItem title="Contact Email" required>
            <SimpleInput
              className="gm-input"
              value={form_data.email}
              onInput={(value) => handleChange('email', value)}
              error={errors.email}
            />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Phone Number" required>
            <SimpleInput
              className="gm-input"
              value={form_data.phone_number}
              onInput={(value) => handleChange('phone_number', value)}
              error={errors.phone_number}
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="Street" required>
            <SimpleInput
              className="gm-input"
              value={form_data.street}
              onInput={(value) => handleChange('street', value)}
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={3}>
        <GridColumn>
          <DetailItem title="Country" required>
            <MultiSelect
              options={COUNTRY_OPTIONS}
              value={selected_country}
              onChange={setSelectedCountry}
              placeholder="Select country"
            />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="State" required>
            <SimpleInput
              className="gm-input"
              value={form_data.state}
              onInput={(value) => handleChange('state', value)}
            />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Zip / Postal Code" required>
            <SimpleInput
              className="gm-input"
              value={form_data.zip}
              onInput={(value) => handleChange('zip', value)}
            />
          </DetailItem>
        </GridColumn>
      </GridRow>

      <Spacer />
      <GridRow num_of_columns={3}>
        <GridColumn />
        <GridColumn />
        <GridColumn>
          <Button
            text="Create organisation"
            loading={saving}
            disabled={saving || !is_valid}
            onClick={submit}
          />
        </GridColumn>
      </GridRow>
    </form>
  );
};
